import {
  getAllStockHoldings,
  getPortfolioTransactions,
  getUserPortfolios,
} from "../../firebase/enhancedFirestoreService";
import {
  getHistoricalData,
  getStockDetailChartTimeParams,
  getStockQuote,
  portfolioChartUsesTimestampPoints,
} from "../stockApi";
import {
  buildPortfolioPeriodChangeSeries,
  computePortfolioPeriodChangeDisplay,
  getPortfolioTimeframeRangeStartStr,
  historicalCloseAtPeriodStart,
  periodChangeDisplayToUsdInternal,
} from "./portfolioPeriodChange";

function convertFirestoreTimestamp(timestamp) {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if (timestamp && typeof timestamp.toDate === "function") {
    return timestamp.toDate();
  }
  if (typeof timestamp === "string") {
    const parsed = new Date(timestamp);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof timestamp === "number") {
    return new Date(timestamp);
  }
  return null;
}

export function filterOwnedStocksFromCache(cachedStockHoldings, portfolioId) {
  if (!cachedStockHoldings?.length) return [];
  if (portfolioId) {
    return cachedStockHoldings.filter(
      (holding) =>
        (Number(holding.totalShares) || 0) > 0 &&
        holding.portfolioId === portfolioId
    );
  }
  const combinedHoldings = [];
  cachedStockHoldings.forEach((holding) => {
    if ((Number(holding.totalShares) || 0) <= 0) return;
    const existing = combinedHoldings.find((h) => h.symbol === holding.symbol);
    if (existing) {
      existing.totalShares += Number(holding.totalShares) || 0;
      existing.totalCost += Number(holding.totalCost) || 0;
      existing.currentValue =
        (Number(existing.currentValue) || 0) +
        (Number(holding.currentValue) || 0);
    } else {
      combinedHoldings.push({
        ...holding,
        totalShares: Number(holding.totalShares) || 0,
        totalCost: Number(holding.totalCost) || 0,
      });
    }
  });
  return combinedHoldings;
}

function mapTimeFrameForHistory(timeFrame) {
  switch (timeFrame) {
    case "5d":
      return "1w";
    case "1w":
      return "1w";
    case "max":
      return "5y";
    default:
      return timeFrame;
  }
}

async function fetchAllPortfolioTransactions(portfolioId, userId) {
  let transactions = [];
  if (portfolioId) {
    const result = await getPortfolioTransactions(portfolioId);
    if (result.success && result.data) transactions = result.data;
    return transactions;
  }
  if (!userId) return [];
  const portfoliosResult = await getUserPortfolios(userId);
  if (!portfoliosResult.success || !portfoliosResult.data) return [];
  for (const portfolio of portfoliosResult.data) {
    const result = await getPortfolioTransactions(portfolio.id);
    if (result.success && result.data?.length) {
      transactions = transactions.concat(result.data);
    }
  }
  return transactions;
}

async function loadOwnedStocksWithChartHistory(
  ownedStocks,
  uiTimeFrame,
  mappedTimeFrame
) {
  const chartParams = getStockDetailChartTimeParams(uiTimeFrame);
  return Promise.all(
    (ownedStocks || []).map(async (investment) => {
      const historicalData = await getHistoricalData(
        investment.symbol,
        mappedTimeFrame,
        { timeSeriesParamsOverride: chartParams }
      );
      return {
        ...investment,
        historicalData: historicalData || [],
        usdRateToUsd: 1,
      };
    })
  );
}

function lookupCachedQuote(cachedBatchQuotes, symbol) {
  const symKey = String(symbol || "").trim().toUpperCase();
  return cachedBatchQuotes?.[symKey] || null;
}

function resolvePeriodEndUnitUsd(
  symbol,
  cachedBatchQuotes,
  historicalLastPrice,
  timeFrame
) {
  const lastHist = Number(historicalLastPrice);
  if (
    String(timeFrame || "").toLowerCase() === "1d" &&
    Number.isFinite(lastHist) &&
    lastHist > 0
  ) {
    return lastHist;
  }
  const quote = lookupCachedQuote(cachedBatchQuotes, symbol);
  const livePx = Number(quote?.price);
  if (Number.isFinite(livePx) && livePx > 0) return livePx;
  if (Number.isFinite(lastHist) && lastHist > 0) return lastHist;
  return 0;
}

function buildPeriodPriceMaps(
  ownedStocks,
  investmentsWithHistory,
  transactions,
  cachedBatchQuotes,
  timeFrame,
  exchangeRateFromUSD
) {
  const fx = Number(exchangeRateFromUSD) > 0 ? Number(exchangeRateFromUSD) : 1;
  const rangeStartStr = getPortfolioTimeframeRangeStartStr(timeFrame);
  const keyFor = (s) => String(s || "").trim().toUpperCase();
  const symbols = new Set();
  for (const stock of ownedStocks || []) {
    const k = keyFor(stock?.symbol);
    if (k) symbols.add(k);
  }
  for (const tx of transactions || []) {
    const type = String(tx?.type || "").toLowerCase();
    if (type !== "buy" && type !== "sell") continue;
    const k = keyFor(tx?.symbol);
    if (k) symbols.add(k);
  }

  const periodStartPriceBySymbol = {};
  const periodEndPriceBySymbol = {};

  for (const sym of symbols) {
    const stock =
      ownedStocks.find((s) => keyFor(s.symbol) === sym) || { symbol: sym };
    const inv = investmentsWithHistory?.find(
      (row) => keyFor(row?.symbol) === sym
    );
    const rawStart = historicalCloseAtPeriodStart(
      inv?.historicalData,
      rangeStartStr,
      timeFrame
    );
    if (Number.isFinite(rawStart) && rawStart > 0) {
      periodStartPriceBySymbol[sym] = rawStart * fx;
    }

    const lastHist =
      inv?.historicalData?.length > 0
        ? Number(inv.historicalData[inv.historicalData.length - 1]?.price)
        : null;
    let unitUsd = resolvePeriodEndUnitUsd(
      sym,
      cachedBatchQuotes,
      lastHist,
      timeFrame
    );
    if (unitUsd <= 0) {
      const cp = Number(stock.currentPrice);
      if (Number.isFinite(cp) && cp > 0) unitUsd = cp;
    }
    if (unitUsd > 0) {
      periodEndPriceBySymbol[sym] = unitUsd * fx;
    }
  }

  return { periodStartPriceBySymbol, periodEndPriceBySymbol };
}

async function fetchBatchQuotes(symbols) {
  const quotes = {};
  await Promise.all(
    symbols.map(async (symbol) => {
      const symKey = String(symbol || "").trim().toUpperCase();
      if (!symKey) return;
      const quote = await getStockQuote(symKey);
      if (quote) quotes[symKey] = quote;
    })
  );
  return quotes;
}

function computePortfolioValueUsd(ownedStocks, batchQuotes) {
  return (ownedStocks || []).reduce((sum, stock) => {
    const symKey = String(stock.symbol || "").trim().toUpperCase();
    const shares = Number(stock.totalShares) || 0;
    const quote = batchQuotes[symKey];
    const px =
      Number(quote?.price) ||
      Number(stock.currentPrice) ||
      (shares > 0 ? Number(stock.currentValue) / shares : 0);
    return sum + shares * px;
  }, 0);
}

/**
 * Load portfolio graph series using the same rules as the mobile PortfolioGraph.
 */
export async function loadPortfolioGraphSeries({
  userId,
  portfolioId = null,
  timeFrame = "1d",
  exchangeRateFromUSD = 1,
}) {
  const holdingsResult = await getAllStockHoldings(userId);
  const cachedStockHoldings = holdingsResult.success ? holdingsResult.data : [];
  const ownedStocks = filterOwnedStocksFromCache(
    cachedStockHoldings,
    portfolioId
  );

  if (!ownedStocks.length) {
    return {
      graphData: [],
      portfolioValueUsd: 0,
      changeUsdInternal: 0,
      percentageChange: 0,
      currencyChangeDisplay: 0,
    };
  }

  const transactions = await fetchAllPortfolioTransactions(portfolioId, userId);
  const mappedTimeFrame = mapTimeFrameForHistory(timeFrame);
  const investmentsWithHistory = await loadOwnedStocksWithChartHistory(
    ownedStocks,
    timeFrame,
    mappedTimeFrame
  );
  const symbols = [...new Set(ownedStocks.map((s) => s.symbol))];
  const batchQuotes = await fetchBatchQuotes(symbols);
  const { periodStartPriceBySymbol, periodEndPriceBySymbol } =
    buildPeriodPriceMaps(
      ownedStocks,
      investmentsWithHistory,
      transactions,
      batchQuotes,
      timeFrame,
      exchangeRateFromUSD
    );

  const usesTimestampChartPoints = portfolioChartUsesTimestampPoints(
    timeFrame,
    mappedTimeFrame
  );
  const allDates = new Set();
  investmentsWithHistory.forEach((investment) => {
    (investment.historicalData || []).forEach((point) => {
      const rawDate = point?.date;
      if (!rawDate) return;
      const key = usesTimestampChartPoints
        ? rawDate
        : String(rawDate).split("T")[0];
      allDates.add(key);
    });
  });
  const sortedDates = Array.from(allDates).sort();

  const graphData =
    sortedDates.length > 0
      ? buildPortfolioPeriodChangeSeries({
          ownedStocks,
          investmentsWithHistory,
          transactions,
          timeFrame,
          convertTimestamp: convertFirestoreTimestamp,
          exchangeRateFromUSD,
          sortedDates,
          usesTimestampChartPoints,
          periodStartPriceBySymbol,
        })
      : [];

  const periodChange = computePortfolioPeriodChangeDisplay({
    ownedStocks,
    transactions,
    timeFrame,
    convertTimestamp: convertFirestoreTimestamp,
    periodStartPriceBySymbol,
    periodEndPriceBySymbol,
  });

  return {
    graphData,
    portfolioValueUsd: computePortfolioValueUsd(ownedStocks, batchQuotes),
    changeUsdInternal: periodChangeDisplayToUsdInternal(
      periodChange.currencyChange,
      exchangeRateFromUSD
    ),
    percentageChange: periodChange.percentageChange,
    currencyChangeDisplay: periodChange.currencyChange,
  };
}
